import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { EventBus, FindState, LinkTarget, PDFFindController, PDFLinkService, PDFViewer, ScrollMode } from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { PdfPosition } from "../lib/api";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 5;

export interface PdfBox {
  page: number;
  h: number;
  v: number;
  width: number;
  height: number;
}

export interface PdfViewerApi {
  zoomIn: () => void;
  zoomOut: () => void;
  previousPage: () => void;
  nextPage: () => void;
  setScale: (value: string) => void;
  /** Full screen, one page at a time, until Esc. */
  present: () => void;
  highlight: (boxes: PdfBox[]) => void;
  /** Top-left position of the current view, in PDF points, for SyncTeX. */
  visiblePoint: () => { page: number; h: number; v: number } | null;
}

interface Props {
  url: string | null;
  scale: string;
  inverted: boolean;
  onScaleChange: (value: string, current: number) => void;
  onPageChange?: (page: number, total: number) => void;
  onSyncToCode: (page: number, h: number, v: number) => void;
  findOpen: boolean;
  onCloseFind: () => void;
  /** Where you were in the PDF last time; the first PDF to load opens there. */
  savedPosition?: () => PdfPosition | null;
  /** Where you are now, as you scroll. */
  onPosition?: (at: PdfPosition) => void;
}

interface FindResult {
  state: number;
  current: number;
  total: number;
}

/** Scrolls to a saved spot, or the top of the last page if the PDF has got shorter since. */
function scrollBack(v: PDFViewer, at: PdfPosition) {
  const page = Math.min(at.page, v.pagesCount);
  const same = page === at.page;
  v.scrollPageIntoView({
    pageNumber: page,
    destArray: [null, { name: "XYZ" }, same ? at.left : 0, same ? at.top : null, null],
    allowNegativeOffset: true,
    ignoreDestinationZoom: true,
  });
}

/** Puts a SyncTeX highlight a third of the way down the view. */
function scrollToMark(el: HTMLElement, mark: HTMLElement) {
  const page = mark.parentElement!;
  el.scrollTo({ top: page.offsetTop + mark.offsetTop - el.clientHeight / 3, behavior: "smooth" });
}

/** A PDF.js viewer that keeps its scroll position and zoom across recompiles, and opens where you were last time. */
export const PdfViewer = forwardRef<PdfViewerApi, Props>(function PdfViewer(props, ref) {
  const container = useRef<HTMLDivElement>(null);
  const viewerDiv = useRef<HTMLDivElement>(null);
  const viewer = useRef<PDFViewer | null>(null);
  const loadToken = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;
  const [error, setError] = useState("");
  const [find, setFind] = useState({ query: "", caseSensitive: false, entireWord: false });
  const [found, setFound] = useState<FindResult | null>(null);
  const findInput = useRef<HTMLInputElement>(null);
  const [presenting, setPresenting] = useState(false);
  // Where you are in the PDF, kept as you scroll so it can be put back when the PDF reappears.
  const at = useRef<PdfPosition | null>(null);
  const hidden = useRef(false);
  // A SyncTeX highlight made while the PDF was hidden, to scroll to when it appears.
  const pendingMark = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const eventBus = new EventBus();
    // A link in the PDF opens a new tab (rel=noopener noreferrer), so it can't swap the editor
    // for a look-alike page, e.g. a fake "signed out" screen.
    const linkService = new PDFLinkService({ eventBus, externalLinkTarget: LinkTarget.BLANK });
    const findController = new PDFFindController({ eventBus, linkService });
    const v = new PDFViewer({
      container: container.current!,
      viewer: viewerDiv.current!,
      eventBus,
      linkService,
      findController,
      removePageBorders: true,
      textLayerMode: 1,
      annotationMode: pdfjs.AnnotationMode.ENABLE,
    });
    linkService.setViewer(v);
    viewer.current = v;
    eventBus.on("scalechanging", (e: { scale: number; presetValue?: string }) => {
      propsRef.current.onScaleChange(e.presetValue ?? String(e.scale), e.scale);
    });
    eventBus.on("pagechanging", (e: { pageNumber: number }) => propsRef.current.onPageChange?.(e.pageNumber, v.pagesCount));
    eventBus.on("updateviewarea", (e: { location?: { pageNumber: number; left: number; top: number } }) => {
      if (!e.location || hidden.current || !container.current!.clientHeight) return;
      at.current = { page: e.location.pageNumber, left: e.location.left, top: e.location.top };
      propsRef.current.onPosition?.(at.current);
    });
    type Counts = { matchesCount: { current: number; total: number } };
    eventBus.on("updatefindcontrolstate", (e: Counts & { state: number }) => setFound({ state: e.state, ...e.matchesCount }));
    eventBus.on("updatefindmatchescount", (e: Counts) => setFound((f) => (f ? { ...f, ...e.matchesCount } : f)));
    return () => {
      v.setDocument(null as never);
      viewer.current = null;
    };
  }, []);

  // Load a new PDF, keeping scroll and zoom.
  useEffect(() => {
    const v = viewer.current;
    if (!v || !props.url) return;
    const token = ++loadToken.current;
    const el = container.current!;
    const keep = v.pagesCount > 0 ? { top: el.scrollTop, left: el.scrollLeft, scale: v.currentScaleValue, page: v.currentPageNumber } : null;
    // The first PDF opens where you were last time, e.g. before a refresh or a trip to History.
    const saved = keep ? null : (at.current ?? propsRef.current.savedPosition?.() ?? null);
    const task = pdfjs.getDocument({
      url: props.url,
      cMapUrl: "/pdfjs/cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "/pdfjs/standard_fonts/",
      wasmUrl: "/pdfjs/wasm/",
      iccUrl: "/pdfjs/iccs/",
    });
    task.promise
      .then((doc) => {
        if (token !== loadToken.current) {
          doc.loadingTask.destroy();
          return;
        }
        setError("");
        // Matches belonged to the old PDF; the next search (Enter) runs on this one.
        setFound(null);
        const onInit = () => {
          v.eventBus.off("pagesinit", onInit);
          v.currentScaleValue = keep?.scale ?? propsRef.current.scale ?? "page-width";
          // A new document resets pdf.js to scrolling; stay on one page while presenting.
          if (document.fullscreenElement === el) {
            v.scrollMode = ScrollMode.PAGE;
            if (keep) v.currentPageNumber = Math.min(keep.page, v.pagesCount);
          } else if (keep) {
            el.scrollTop = keep.top;
            el.scrollLeft = keep.left;
          } else if (saved) {
            at.current = saved;
            // A hidden PDF is scrolled there when it appears.
            if (!hidden.current) scrollBack(v, saved);
          }
          propsRef.current.onPageChange?.(v.currentPageNumber, v.pagesCount);
        };
        v.eventBus.on("pagesinit", onInit);
        const old = v.pdfDocument;
        v.setDocument(doc);
        (v as unknown as { linkService: PDFLinkService }).linkService.setDocument(doc, null);
        old?.loadingTask.destroy();
      })
      .catch((err) => {
        if (token === loadToken.current) setError(String(err?.message ?? err));
      });
    return () => {
      if (token !== loadToken.current) task.destroy();
    };
  }, [props.url]);

  useEffect(() => {
    const v = viewer.current;
    if (v && v.pagesCount > 0 && props.scale && v.currentScaleValue !== props.scale) v.currentScaleValue = props.scale;
  }, [props.scale]);

  // Hiding the PDF (the editor-only layout, or the code on a phone) loses its scroll position, so it's put
  // back when the PDF reappears, unless a SyncTeX jump landed while it was hidden.
  useEffect(() => {
    const el = container.current!;
    hidden.current = !el.clientHeight;
    const visibility = new ResizeObserver(() => {
      const was = hidden.current;
      hidden.current = !el.clientHeight;
      const v = viewer.current;
      if (!was || hidden.current || !v || v.pagesCount === 0) return;
      if (pendingMark.current?.isConnected) scrollToMark(el, pendingMark.current);
      else if (at.current) scrollBack(v, at.current);
      pendingMark.current = null;
    });
    visibility.observe(el);
    return () => visibility.disconnect();
  }, []);

  // ---------------------------------------------------------------- find

  const runFind = (type: "" | "again", previous = false, opts = find) => {
    if (!opts.query) return;
    viewer.current?.eventBus.dispatch("find", {
      source: null,
      type,
      query: opts.query,
      caseSensitive: opts.caseSensitive,
      entireWord: opts.entireWord,
      highlightAll: true,
      findPrevious: previous,
      matchDiacritics: false,
    });
  };
  const updateFind = (patch: Partial<typeof find>) => {
    const next = { ...find, ...patch };
    setFind(next);
    if (next.query) runFind("", false, next);
    else {
      viewer.current?.eventBus.dispatch("findbarclose", { source: null });
      setFound(null);
    }
  };

  useEffect(() => {
    if (props.findOpen) {
      // Start from text selected in the PDF, like a browser's find bar.
      const sel = window.getSelection();
      const picked = sel && container.current?.contains(sel.anchorNode) ? sel.toString().trim() : "";
      if (picked && picked.length <= 200 && !picked.includes("\n")) updateFind({ query: picked });
      // preventScroll: focusing mustn't scroll the (overflow: hidden) workspace sideways on phones.
      findInput.current?.focus({ preventScroll: true });
      findInput.current?.select();
    } else {
      viewer.current?.eventBus.dispatch("findbarclose", { source: null });
      setFound(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.findOpen]);

  const findLabel = !find.query
    ? ""
    : !found
      ? ""
      : found.state === FindState.PENDING && found.total === 0
        ? "…"
        : found.total === 0
          ? "No matches"
          : `${found.current || "–"} of ${found.total}${found.state === FindState.WRAPPED ? " ↺" : ""}`;
  const noMatches = !!find.query && found?.total === 0 && found.state === FindState.NOT_FOUND;

  // Pinch to zoom, keeping the point under the fingers/cursor still. Trackpad pinches and
  // ctrl/cmd+scroll arrive as ctrl+wheel, Safari sends gesture events (for trackpads and
  // touch alike), and other touchscreens send two-finger touches.
  useEffect(() => {
    const el = container.current!;
    // pdf.js rounds scales to 0.01, so hold back tiny steps until they add up.
    let pending = 1;
    const zoomAt = (factor: number, x: number, y: number) => {
      const v = viewer.current;
      if (!v || v.pagesCount === 0) return;
      const cur = v.currentScale;
      const target = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, cur * pending * factor));
      pending = target / cur;
      if (Math.abs(target - cur) < 0.01) return;
      // pdf.js measures the origin from the container's offset parent, not the viewport.
      const r = el.getBoundingClientRect();
      v.updateScale({ scaleFactor: pending, origin: [x - r.left + el.offsetLeft, y - r.top + el.offsetTop], drawingDelay: 300 });
      pending = 1;
    };

    let gesture: number | null = null;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (gesture !== null) return;
      const dy = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaMode === WheelEvent.DOM_DELTA_PAGE ? e.deltaY * 400 : e.deltaY;
      // Pinches send many small deltas; a mouse wheel notch is one big one, capped to about a button step.
      zoomAt(Math.exp(-Math.max(-15, Math.min(15, dy)) / 100), e.clientX, e.clientY);
    };
    type GestureEvent = UIEvent & { scale: number; clientX: number; clientY: number };
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      gesture = 1;
    };
    const onGestureChange = (e: Event) => {
      const g = e as GestureEvent;
      e.preventDefault();
      if (gesture === null) return;
      zoomAt(g.scale / gesture, g.clientX, g.clientY);
      gesture = g.scale;
    };
    const onGestureEnd = (e: Event) => {
      e.preventDefault();
      gesture = null;
    };

    let touchDist = 0;
    const spread = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) touchDist = spread(e.touches);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !touchDist) return;
      e.preventDefault();
      const d = spread(e.touches);
      zoomAt(d / touchDist, (e.touches[0].clientX + e.touches[1].clientX) / 2, (e.touches[0].clientY + e.touches[1].clientY) / 2);
      touchDist = d;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) touchDist = 0;
    };

    const hasGestures = "ongesturestart" in window;
    el.addEventListener("wheel", onWheel, { passive: false });
    if (hasGestures) {
      el.addEventListener("gesturestart", onGestureStart);
      el.addEventListener("gesturechange", onGestureChange);
      el.addEventListener("gestureend", onGestureEnd);
    } else {
      el.addEventListener("touchstart", onTouchStart, { passive: true });
      el.addEventListener("touchmove", onTouchMove, { passive: false });
      el.addEventListener("touchend", onTouchEnd);
      el.addEventListener("touchcancel", onTouchEnd);
    }
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGestureStart);
      el.removeEventListener("gesturechange", onGestureChange);
      el.removeEventListener("gestureend", onGestureEnd);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  // ---------------------------------------------------------------- presentation mode

  // Full screen shows one fitted page at a time; leaving puts the zoom and scrolling back,
  // on whichever page was showing.
  useEffect(() => {
    const el = container.current!;
    let before: string | null = null;
    const onChange = () => {
      const v = viewer.current;
      if (!v) return;
      if (document.fullscreenElement === el) {
        before = v.currentScaleValue;
        setPresenting(true);
        el.focus({ preventScroll: true });
        v.scrollMode = ScrollMode.PAGE;
        // Fit once the element has its new size.
        requestAnimationFrame(() => (v.currentScaleValue = "page-fit"));
      } else if (before !== null) {
        const scale = before;
        before = null;
        setPresenting(false);
        requestAnimationFrame(() => {
          const page = v.currentPageNumber;
          v.scrollMode = ScrollMode.VERTICAL;
          v.currentScaleValue = scale;
          v.currentPageNumber = page;
        });
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (!presenting) return;
    const el = container.current!;
    const onKey = (e: KeyboardEvent) => {
      const v = viewer.current;
      if (!v || e.metaKey || e.ctrlKey || e.altKey) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"].includes(e.key)) v.nextPage();
      else if (["ArrowLeft", "ArrowUp", "PageUp", "Backspace"].includes(e.key)) v.previousPage();
      else if (e.key === "Home") v.currentPageNumber = 1;
      else if (e.key === "End") v.currentPageNumber = v.pagesCount;
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    // One page per spin of the wheel or flick of the trackpad: only the first event of a burst
    // turns the page, so momentum scrolling doesn't run through the slides.
    let lastWheel = 0;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const fresh = e.timeStamp - lastWheel > 250;
      lastWheel = e.timeStamp;
      const d = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (fresh && d > 0) viewer.current?.nextPage();
      else if (fresh && d < 0) viewer.current?.previousPage();
    };
    window.addEventListener("keydown", onKey, true);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKey, true);
      el.removeEventListener("wheel", onWheel);
    };
  }, [presenting]);

  useImperativeHandle(ref, () => ({
    zoomIn() {
      const v = viewer.current;
      if (v) v.currentScale = Math.min(MAX_ZOOM, v.currentScale * 1.15);
    },
    zoomOut() {
      const v = viewer.current;
      if (v) v.currentScale = Math.max(MIN_ZOOM, v.currentScale / 1.15);
    },
    previousPage() {
      viewer.current?.previousPage();
    },
    nextPage() {
      viewer.current?.nextPage();
    },
    setScale(value) {
      const v = viewer.current;
      if (v) v.currentScaleValue = value;
    },
    present() {
      const v = viewer.current;
      if (v && v.pagesCount > 0) container.current!.requestFullscreen().catch(() => {});
    },
    highlight(boxes) {
      const v = viewer.current;
      if (!v || boxes.length === 0) return;
      container.current!.querySelectorAll(".synctex-highlight").forEach((n) => n.remove());
      let first: HTMLElement | null = null;
      for (const b of boxes) {
        const pageView = v.getPageView(b.page - 1) as { div: HTMLDivElement; viewport: pdfjs.PageViewport; pdfPage?: pdfjs.PDFPageProxy } | undefined;
        if (!pageView) continue;
        const vp = pageView.viewport;
        const pageHeight = vp.viewBox[3] - vp.viewBox[1];
        const [x1, y1] = vp.convertToViewportPoint(b.h, pageHeight - (b.v - b.height));
        const [x2, y2] = vp.convertToViewportPoint(b.h + b.width, pageHeight - b.v);
        const div = document.createElement("div");
        div.className = "synctex-highlight";
        Object.assign(div.style, {
          left: `${Math.min(x1, x2) - 2}px`,
          top: `${Math.min(y1, y2) - 2}px`,
          width: `${Math.abs(x2 - x1) + 4}px`,
          height: `${Math.abs(y2 - y1) + 4}px`,
        });
        pageView.div.appendChild(div);
        first ??= div;
      }
      if (first && hidden.current) pendingMark.current = first;
      else if (first) scrollToMark(container.current!, first);
    },
    visiblePoint() {
      const v = viewer.current;
      if (!v || v.pagesCount === 0) return null;
      const pageView = v.getPageView(v.currentPageNumber - 1) as { div: HTMLDivElement; viewport: pdfjs.PageViewport };
      const el = container.current!;
      const y = Math.max(0, el.scrollTop + el.clientHeight / 3 - pageView.div.offsetTop);
      const [px, py] = pageView.viewport.convertToPdfPoint(pageView.div.clientWidth / 2, y);
      const pageHeight = pageView.viewport.viewBox[3] - pageView.viewport.viewBox[1];
      return { page: v.currentPageNumber, h: px, v: pageHeight - py };
    },
  }));

  return (
    <>
    <div
      className={`pdf-host${props.inverted ? " inverted" : ""}`}
      ref={container}
      tabIndex={-1}
      onClick={(e) => {
        // Presenting: a click turns the page (Shift goes back), except on a link.
        if (!presenting || (e.target as HTMLElement).closest("a")) return;
        if (e.shiftKey) viewer.current?.previousPage();
        else viewer.current?.nextPage();
      }}
      onDoubleClick={(e) => {
        const v = viewer.current;
        const pageEl = (e.target as HTMLElement).closest(".page") as HTMLElement | null;
        if (!v || !pageEl || presenting) return;
        const pageNumber = Number(pageEl.dataset.pageNumber);
        const pageView = v.getPageView(pageNumber - 1) as { viewport: pdfjs.PageViewport };
        const rect = pageEl.getBoundingClientRect();
        const [px, py] = pageView.viewport.convertToPdfPoint(e.clientX - rect.left, e.clientY - rect.top);
        const pageHeight = pageView.viewport.viewBox[3] - pageView.viewport.viewBox[1];
        window.getSelection()?.removeAllRanges();
        props.onSyncToCode(pageNumber, px, pageHeight - py);
      }}
    >
      <div ref={viewerDiv} className="pdfViewer" />
      {error && (
        <div className="pdf-placeholder">
          <div>
            <div className="big">Couldn't show the PDF</div>
            <div className="small">{error}</div>
          </div>
        </div>
      )}
    </div>
    {props.findOpen && (
      <div className="pdf-find find-bar" role="search">
        <div className="find-row">
          <div className={`find-input${noMatches ? " none" : ""}`}>
            <input
              ref={findInput}
              type="search"
              placeholder="Search the PDF"
              aria-label="Search the PDF"
              value={find.query}
              onChange={(e) => updateFind({ query: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  runFind("again", e.shiftKey);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  props.onCloseFind();
                }
              }}
            />
            <button
              type="button"
              className={`find-toggle${find.caseSensitive ? " on" : ""}`}
              title="Match case"
              aria-pressed={find.caseSensitive}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateFind({ caseSensitive: !find.caseSensitive })}
            >
              Aa
            </button>
            <button
              type="button"
              className={`find-toggle bold${find.entireWord ? " on" : ""}`}
              title="Whole words"
              aria-pressed={find.entireWord}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateFind({ entireWord: !find.entireWord })}
            >
              W
            </button>
          </div>
          <div className="find-group">
            <button type="button" className="icon-btn" title="Previous match (Shift+Enter)" aria-label="Previous match" disabled={!find.query} onClick={() => runFind("again", true)}>
              <ChevronUp size={16} />
            </button>
            <button type="button" className="icon-btn" title="Next match (Enter)" aria-label="Next match" disabled={!find.query} onClick={() => runFind("again")}>
              <ChevronDown size={16} />
            </button>
          </div>
          <span className={`find-count${noMatches ? " none" : ""}`} aria-live="polite">
            {findLabel}
          </span>
          <button type="button" className="icon-btn find-close" title="Close (Esc)" aria-label="Close" onClick={props.onCloseFind}>
            <X size={16} />
          </button>
        </div>
      </div>
    )}
    </>
  );
});
